// =============================================================================
// FILE: packages/renderer/src/hooks/useFeatureRegistry.ts
// PURPOSE: Renderer-side accessor for the shared FeatureRegistry. Wraps the
//          static registry so future iterations can layer License-Server
//          overrides on top without changing call sites.
// =============================================================================

import { useMemo } from 'react';
import {
  FEATURE_REGISTRY, getFeature, getFeatureState, isFeatureInteractive,
  type FeatureEntry, type FeatureState,
} from '@bim/shared/config/feature.registry.js';

export type { FeatureEntry, FeatureState };

export function useFeatureRegistry(): {
  features:        ReadonlyArray<FeatureEntry>;
  byId:            (id: string) => FeatureEntry | undefined;
  state:           (id: string) => FeatureState;
  isInteractive:   (id: string) => boolean;
  isActive:        (id: string) => boolean;
  isExperimental:  (id: string) => boolean;
  isLocked:        (id: string) => boolean;
  isDisabled:      (id: string) => boolean;
} {
  return useMemo(() => ({
    features:       FEATURE_REGISTRY,
    byId:           getFeature,
    state:          getFeatureState,
    isInteractive:  isFeatureInteractive,
    isActive:       (id) => getFeatureState(id) === 'ACTIVE',
    isExperimental: (id) => getFeatureState(id) === 'EXPERIMENTAL',
    isLocked:       (id) => getFeatureState(id) === 'LOCKED',
    isDisabled:     (id) => getFeatureState(id) === 'DISABLED',
  }), []);
}
